import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Share2 } from "lucide-react";
import { Button } from "./ui/button";
import { WAITLIST_CTA } from "../data/home-data";

const LAUNCH = new Date(WAITLIST_CTA.launchDate).getTime();

function useCountdown() {
    const [t, setT] = useState({ d: 0, h: 0, m: 0, s: 0 });
    useEffect(() => {
        const update = () => {
            const diff = Math.max(0, LAUNCH - Date.now());
            setT({
                d: Math.floor(diff / 86400000),
                h: Math.floor((diff / 3600000) % 24),
                m: Math.floor((diff / 60000) % 60),
                s: Math.floor((diff / 1000) % 60),
            });
        };
        update();
        const i = setInterval(update, 1000);
        return () => clearInterval(i);
    }, []);
    return t;
}

const BENEFITS = WAITLIST_CTA.benefits;

export default function WaitlistCTA() {
    const navigate = useNavigate();
    const c = useCountdown();
    const share = () => {
        const url = window.location.origin;
        const text = WAITLIST_CTA.shareText;
        if (navigator.share) navigator.share({
            title: WAITLIST_CTA.shareTitle, text, url
        }).catch(() => { });
        else { navigator.clipboard.writeText(`${text} ${url}`); }
    };

    return (
        <section className="py-28 bg-gradient-to-br from-aisky/60 via-ailavender/60 to-aipeach/60" data-testid="waitlist-cta-section">
            <div className="max-w-6xl mx-auto px-4 sm:px-8">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                    <div >
                        <p className="text-xs uppercase tracking-[0.3em] text-aiorange font-bold mb-3">{WAITLIST_CTA.eyebrow}</p>
                        <h2 className="font-display font-black text-4xl sm:text-5xl tracking-normal leading-[1.05]">{WAITLIST_CTA.headlineLine1}<br /><span className="text-aiblue">{WAITLIST_CTA.headlineLine2}</span></h2>
                        <p className="mt-5 text-lg text-gray-700">{WAITLIST_CTA.description}</p>

                        {/* Countdown */}
                        <div className="mt-8 grid grid-cols-4 gap-3" data-testid="countdown-timer">
                            {
                                [[WAITLIST_CTA.countdownLabels[0], c.d], [WAITLIST_CTA.countdownLabels[1], c.h], [WAITLIST_CTA.countdownLabels[2], c.m], [WAITLIST_CTA.countdownLabels[3], c.s]].map(([l, v]) => (
                                    <div key={l} className="bg-white/80 backdrop-blur rounded-2xl p-4 text-center">
                                        <div className="font-mono font-black text-3xl text-aiblue tabular-nums">{String(v).padStart(2, "0")}</div>
                                        <div className="text-[10px] uppercase tracking-widest text-gray-500 mt-1">{l}</div>
                                    </div >
                                ))
                            }
                        </div >

                        <div className="mt-8 flex flex-wrap gap-3">
                            <Button onClick={() => navigate("/waitlist?role=student")} className="bg-aiblue hover:bg-blue-700 text-white rounded-full px-7 py-6 font-semibold" data-testid="waitlist-claim-spot-btn">
                                {WAITLIST_CTA.claimSpotCta} <ArrowRight className="ml-2 h-4 w-4" />
                            </Button >
                            <Button variant="outline" onClick={share} className="rounded-full px-7 py-6 font-semibold" data-testid="waitlist-share-btn">
                                <Share2 className="mr-2 h-4 w-4" /> {WAITLIST_CTA.shareCta}
                            </Button >
                        </div >
                    </div >

                    {/* Benefits */}
                    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="glass-card rounded-3xl p-8">
                        <p className="text-xs uppercase tracking-widest font-bold text-aiblue mb-5">{WAITLIST_CTA.benefitsHeading}</p>
                        <ul className="space-y-4">
                            {
                                BENEFITS.map((b, i) => (
                                    <li key={i} className="flex gap-3 items-start">
                                        <span className="h-7 w-7 rounded-full bg-aiblue text-white flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">{i + 1}</span>
                                        <span className="text-base">{b}</span>
                                    </li >
                                ))}
                        </ul >
                    </motion.div >
                </div >
            </div >
        </section >
    );
}
